import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Printer, Download, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

export default function InvoiceOldView() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const invoiceRef = useRef(null);
  
  const [invoice, setInvoice] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Obtener datos de la factura OLD
        const invoiceRes = await api.get(`/invoices/old/${invoiceId}`);
        setInvoice(invoiceRes.data);
        
        // Obtener datos del lote
        if (invoiceRes.data.batch_id) {
          try {
            const batchRes = await api.get(`/batches/${invoiceRes.data.batch_id}`);
            setBatch(batchRes.data);
          } catch (e) {
            console.warn("No se pudo cargar el lote");
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Error al cargar la factura");
        toast.error("Error al cargar la factura");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [invoiceId]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;
    
    setSavingPdf(true);
    try {
      const element = invoiceRef.current;
      const opt = {
        margin: 10,
        filename: `${invoice?.nombre_archivo || 'factura'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success("PDF descargado correctamente");
    } catch (err) {
      toast.error("Error al generar PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
        <span className="ml-2">Cargando factura...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-gray-600 mb-4">Factura no encontrada</p>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  const formatCurrency = (amount, currency = "USD") => {
    if (!amount) return "-";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dateStr;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Barra de herramientas */}
      <div className="print:hidden bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div className="text-sm">
            <span className="font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded">
              VERSIÓN ARCHIVADA (OLD)
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          <Button onClick={handleDownloadPDF} disabled={savingPdf}>
            {savingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Descargar PDF
          </Button>
        </div>
      </div>

      {/* Contenido de la factura */}
      <div className="p-8">
        <div 
          ref={invoiceRef}
          className="max-w-4xl mx-auto bg-white shadow-lg p-8"
          style={{ minHeight: "297mm" }}
        >
          {/* Cabecera */}
          <div className="border-b-2 border-gray-300 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {invoice.tipo === "origen" ? "FACTURA ORIGEN" : "FACTURA CLIENTE"}
                </h1>
                <p className="text-lg font-semibold text-blue-600 mt-2">
                  Nº {invoice.numero_factura}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Fecha Factura</p>
                <p className="font-semibold">{formatDate(invoice.fecha_factura)}</p>
                <p className="text-sm text-gray-600 mt-2">Vencimiento</p>
                <p className="font-semibold">{formatDate(invoice.fecha_vencimiento)}</p>
              </div>
            </div>
          </div>

          {/* Emisor y Receptor */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Emisor</h3>
              <p className="font-bold text-lg">{invoice.emisor_nombre}</p>
              <p className="text-gray-600">{invoice.emisor_direccion}</p>
              <p className="text-gray-600">{invoice.emisor_nif}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Receptor</h3>
              <p className="font-bold text-lg">{invoice.receptor_nombre}</p>
              <p className="text-gray-600">{invoice.receptor_direccion}</p>
              <p className="text-gray-600">{invoice.receptor_nif}</p>
            </div>
          </div>

          {/* Información del lote */}
          <div className="bg-gray-50 p-4 rounded mb-8">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Lote:</span>
                <span className="font-semibold ml-2">{invoice.numero_lote || batch?.numero_lote || "-"}</span>
              </div>
              <div>
                <span className="text-gray-500">Contenedor:</span>
                <span className="font-semibold ml-2">{invoice.contenedor || batch?.contenedor || "-"}</span>
              </div>
              <div>
                <span className="text-gray-500">Producto:</span>
                <span className="font-semibold ml-2">{invoice.producto || batch?.producto_nombre || "-"}</span>
              </div>
            </div>
          </div>

          {/* Detalle de la factura */}
          <table className="w-full mb-8">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-2">Descripción</th>
                <th className="text-right py-2">Cantidad (kg)</th>
                <th className="text-right py-2">Precio/kg</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-3">{invoice.producto || "Producto"}</td>
                <td className="text-right py-3">{invoice.cantidad?.toFixed(2) || "-"}</td>
                <td className="text-right py-3">{formatCurrency(invoice.precio_unitario)}</td>
                <td className="text-right py-3 font-semibold">{formatCurrency(invoice.subtotal)}</td>
              </tr>
            </tbody>
          </table>

          {/* Totales */}
          <div className="flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold">{formatCurrency(invoice.subtotal)}</span>
              </div>
              {invoice.iva > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">IVA ({invoice.iva}%):</span>
                  <span className="font-semibold">{formatCurrency(invoice.iva_importe)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 text-lg">
                <span className="font-bold">TOTAL:</span>
                <span className="font-bold text-blue-600">{formatCurrency(invoice.total)}</span>
              </div>
            </div>
          </div>

          {/* Observaciones */}
          {invoice.observaciones && (
            <div className="mt-8 p-4 bg-gray-50 rounded">
              <h4 className="font-semibold text-gray-700 mb-2">Observaciones:</h4>
              <p className="text-gray-600">{invoice.observaciones}</p>
            </div>
          )}

          {/* Pie de página */}
          <div className="mt-auto pt-8 border-t text-center text-sm text-gray-500">
            <p>Versión archivada: {invoice.nombre_archivo}</p>
            <p>Archivada el: {new Date(invoice.modified_at).toLocaleString("es-ES")}</p>
          </div>
        </div>
      </div>

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
